import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import { Box, Text, Heading } from '@primer/react';
import ServiceHero from '../components/ServiceHero';
import ServiceCTA from '../components/ServiceCTA';
import Reveal from '../components/Reveal';
import SEO from '../components/SEO';
import { workProjects } from '../data/workProjects';
import { breadcrumbSchema, articleSchema } from '../utils/seo';

function WorkDetail() {
  const { slug } = useParams();
  const project = workProjects[slug];

  if (!project) {
    return <Navigate to="/" replace />;
  }

  const schema = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: project.client, path: `/work/${project.slug}` },
    ]),
    articleSchema({
      title: project.seoTitle,
      description: project.seoDescription,
      path: `/work/${project.slug}`,
    }),
  ];

  return (
    <Box sx={{ backgroundColor: 'canvas.default' }}>
      <SEO
        title={project.seoTitle}
        description={project.seoDescription}
        path={`/work/${project.slug}`}
        schema={schema}
      />

      <ServiceHero
        headline={project.headline}
        subtitle={project.subtitle}
        secondaryCtaLabel="View Services"
        secondaryCtaHref="/services"
      />

      <Box sx={{ paddingX: ['24px', '48px', '100px'], paddingBottom: [6, 7, 7] }}>
        <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
          <Reveal>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: [4, 6, 7],
                paddingY: 4,
                borderTop: '1px solid',
                borderTopColor: 'border.default',
                borderBottom: '1px solid',
                borderBottomColor: 'border.default',
              }}
            >
              <MetaItem label="Client" value={project.client} />
              <MetaItem label="Focus" value={project.category} />
              <MetaItem label="Role" value={project.role} />
              <MetaItem label="Timeline" value={project.timeline} />
              <MetaItem label="Result" value={project.stat} highlight />
            </Box>
          </Reveal>
        </Box>
      </Box>

      {project.heroImage && (
        <Reveal>
          <Box sx={{ paddingX: ['24px', '48px', '100px'], marginBottom: [6, 7, 8] }}>
            <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
              <Box
                as="img"
                src={project.heroImage}
                alt={`${project.client} project overview`}
                sx={{ width: '100%', borderRadius: '16px', display: 'block' }}
              />
            </Box>
          </Box>
        </Reveal>
      )}

      <Box sx={{ paddingX: ['24px', '48px', '100px'] }}>
        <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
          {project.sections.map((section, index) => (
            <Reveal key={section.id} delay={Math.min(index * 0.05, 0.2)}>
              <Box id={section.id} sx={{ marginBottom: [7, 8, 8] }}>
                <Text
                  as="p"
                  sx={{
                    margin: 0,
                    marginBottom: 2,
                    fontSize: 1,
                    fontWeight: 600,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: 'accent.fg',
                  }}
                >
                  {section.eyebrow}
                </Text>
                <Heading
                  as="h2"
                  sx={{
                    margin: 0,
                    marginBottom: 4,
                    fontFamily: "'Fraunces', serif",
                    fontWeight: 300,
                    fontSize: ['1.6rem', '2rem', '2.25rem'],
                    lineHeight: 1.3,
                    color: 'fg.default',
                  }}
                >
                  {section.title}
                </Heading>

                {section.paragraphs.map((paragraph) => (
                  <Text
                    key={paragraph.slice(0, 40)}
                    as="p"
                    sx={{
                      margin: 0,
                      marginBottom: 4,
                      fontSize: 2,
                      lineHeight: 1.75,
                      color: 'fg.muted',
                    }}
                  >
                    {paragraph}
                  </Text>
                ))}

                {section.beforeImage && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: ['1fr', '1fr 1fr'], gap: 3, marginTop: 4 }}>
                    <Box>
                      <Text as="p" sx={{ margin: 0, marginBottom: 2, fontSize: 0, color: 'fg.subtle' }}>
                        Before
                      </Text>
                      <Box as="img" src={section.beforeImage} alt="Before" sx={{ width: '100%', borderRadius: '12px' }} />
                    </Box>
                    <Box>
                      <Text as="p" sx={{ margin: 0, marginBottom: 2, fontSize: 0, color: 'fg.subtle' }}>
                        After
                      </Text>
                      <Box as="img" src={section.image} alt="After" sx={{ width: '100%', borderRadius: '12px' }} />
                    </Box>
                  </Box>
                )}

                {section.image && !section.beforeImage && (
                  <Box sx={{ marginTop: 4 }}>
                    <Box
                      as="img"
                      src={section.image}
                      alt={section.imageCaption || section.title}
                      sx={{ width: '100%', borderRadius: '12px', display: 'block' }}
                    />
                    {section.imageCaption && (
                      <Text
                        as="p"
                        sx={{ margin: 0, marginTop: 2, fontSize: 0, color: 'fg.subtle' }}
                      >
                        {section.imageCaption}
                      </Text>
                    )}
                  </Box>
                )}

                {section.images && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                    {section.images.map((img) => (
                      <Box key={img.src}>
                        <Box
                          as="img"
                          src={img.src}
                          alt={img.caption || section.title}
                          sx={{ width: '100%', borderRadius: '12px', display: 'block' }}
                        />
                        {img.caption && (
                          <Text
                            as="p"
                            sx={{ margin: 0, marginTop: 2, fontSize: 0, color: 'fg.subtle' }}
                          >
                            {img.caption}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Reveal>
          ))}

          <Reveal>
            <Box
              as={RouterLink}
              to="/"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                marginBottom: [7, 8, 8],
                fontSize: 1,
                fontWeight: 500,
                color: 'fg.muted',
                textDecoration: 'none',
                ':hover': { color: 'fg.default' },
              }}
            >
              &larr; Back to all work
            </Box>
          </Reveal>
        </Box>
      </Box>

      <ServiceCTA
        eyebrow="Like what you see?"
        headline={[
          { text: "Let's build your", tone: 'plain' },
          { text: 'next case study.', tone: 'gradient' },
        ]}
        subtitle="Book a consultation and let's talk through what a Pegasus UX engagement would look like for your product."
      />
    </Box>
  );
}

function MetaItem({ label, value, highlight = false }) {
  return (
    <Box>
      <Text
        as="p"
        sx={{
          margin: 0,
          marginBottom: 1,
          fontSize: 0,
          fontWeight: 600,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'fg.subtle',
        }}
      >
        {label}
      </Text>
      <Text
        as="p"
        sx={{
          margin: 0,
          fontSize: 2,
          fontWeight: highlight ? 600 : 400,
          color: highlight ? 'accent.fg' : 'fg.default',
        }}
      >
        {value}
      </Text>
    </Box>
  );
}

export default WorkDetail;
